import { jsPDF } from 'jspdf';
import { BRAND_FOOTER, BRAND_NAME, LOGO_URL } from '@/lib/brand';
import { formatCurrencyPlain, formatDate } from '@/lib/format';
import type { Receipt } from '@/types';

export interface ReceiptPdfData {
  receiptNumber: string;
  date: string;
  clientName: string;
  clientPhone: string;
  projectName: string;
  concept: string;
  description: string | null;
  amount: number;
  observation: string | null;
  generatedBy: string;
}

const NAVY: [number, number, number] = [23, 42, 74];
const GREEN: [number, number, number] = [22, 145, 100];
const DARK: [number, number, number] = [30, 41, 59];
const MUTED: [number, number, number] = [110, 125, 145];
const LIGHT: [number, number, number] = [244, 247, 249];
const BORDER: [number, number, number] = [222, 229, 235];
const WHITE: [number, number, number] = [255, 255, 255];

async function loadLogoDataUrl(): Promise<{ url: string; format: 'PNG' | 'JPEG' } | null> {
  try {
    const response = await fetch(LOGO_URL, { mode: 'cors' });
    if (!response.ok) return null;
    const blob = await response.blob();
    const dataUrl = await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
    if (!dataUrl) return null;
    const format = dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
    return { url: dataUrl, format };
  } catch {
    return null;
  }
}

function text(doc: jsPDF, value: string, x: number, y: number, opts?: { size?: number; bold?: boolean; color?: [number, number, number]; align?: 'left' | 'right' | 'center' }) {
  doc.setFont('helvetica', opts?.bold ? 'bold' : 'normal');
  doc.setFontSize(opts?.size ?? 10);
  const color = opts?.color ?? DARK;
  doc.setTextColor(color[0], color[1], color[2]);
  doc.text(value, x, y, { align: opts?.align ?? 'left' });
}

export function receiptFileName(receiptNumber: string): string {
  return `Recibo_${receiptNumber}.pdf`;
}

export function toReceiptPdfData(receipt: Receipt, generatedBy: string): ReceiptPdfData {
  return {
    receiptNumber: receipt.receipt_number,
    date: receipt.receipt_date,
    clientName: receipt.projects?.clients?.name ?? 'Sin cliente',
    clientPhone: receipt.projects?.clients?.contact_phone ?? '—',
    projectName: receipt.projects?.name ?? 'Sin proyecto',
    concept: receipt.concept,
    description: receipt.description,
    amount: Number(receipt.amount),
    observation: receipt.observation,
    generatedBy: generatedBy || receipt.created_by_name || 'Usuario',
  };
}

/** Construye el PDF profesional del recibo listo para ver, imprimir o descargar. */
export async function buildReceiptPdf(data: ReceiptPdfData): Promise<jsPDF> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = 210;
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;

  // Banda superior corporativa
  doc.setFillColor(NAVY[0], NAVY[1], NAVY[2]);
  doc.rect(0, 0, pageWidth, 36, 'F');
  doc.setFillColor(GREEN[0], GREEN[1], GREEN[2]);
  doc.rect(0, 36, pageWidth, 1.6, 'F');

  const logo = await loadLogoDataUrl();
  let brandX = margin;
  if (logo) {
    try {
      doc.addImage(logo.url, logo.format, margin, 8, 20, 20);
      brandX = margin + 25;
    } catch {
      brandX = margin;
    }
  }
  text(doc, BRAND_NAME, brandX, 16, { size: 17, bold: true, color: WHITE });
  text(doc, 'Sistema de Seguimiento', brandX, 22, { size: 9, color: [205, 216, 228] });
  text(doc, 'RECIBO', pageWidth - margin, 18, { size: 22, bold: true, color: WHITE, align: 'right' });

  // Número y fecha
  let y = 54;
  text(doc, `N° ${data.receiptNumber}`, margin, y, { size: 13, bold: true, color: NAVY });
  text(doc, `Fecha: ${formatDate(data.date)}`, pageWidth - margin, y, {
    size: 11,
    color: MUTED,
    align: 'right',
  });

  // Bloque de datos
  y += 8;
  const boxH = 34;
  doc.setFillColor(LIGHT[0], LIGHT[1], LIGHT[2]);
  doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, y, contentWidth, boxH, 2, 2, 'FD');

  const colLeft = margin + 6;
  const colRight = margin + contentWidth / 2 + 6;
  let labelY = y + 9;
  text(doc, 'CLIENTE', colLeft, labelY, { size: 8, bold: true, color: MUTED });
  text(doc, 'PROYECTO', colRight, labelY, { size: 8, bold: true, color: MUTED });
  labelY += 5.5;
  text(doc, data.clientName, colLeft, labelY, { size: 11, bold: true, color: DARK });
  const projectLines = doc.splitTextToSize(data.projectName, contentWidth / 2 - 12);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(DARK[0], DARK[1], DARK[2]);
  doc.text(projectLines[0] ?? '', colRight, labelY);

  labelY += 9;
  text(doc, 'CELULAR', colLeft, labelY, { size: 8, bold: true, color: MUTED });
  labelY += 5.5;
  text(doc, data.clientPhone || '—', colLeft, labelY, { size: 11, color: DARK });

  // Tabla de detalle
  y += boxH + 12;
  const tableX = margin;
  const tableW = contentWidth;
  const headerH = 10;
  doc.setFillColor(NAVY[0], NAVY[1], NAVY[2]);
  doc.rect(tableX, y, tableW, headerH, 'F');
  text(doc, 'CONCEPTO', tableX + 5, y + 6.5, { size: 9, bold: true, color: WHITE });
  text(doc, 'DESCRIPCIÓN', tableX + 62, y + 6.5, { size: 9, bold: true, color: WHITE });
  text(doc, 'MONTO (Bs.)', tableX + tableW - 5, y + 6.5, {
    size: 9,
    bold: true,
    color: WHITE,
    align: 'right',
  });

  const descText = data.description && data.description.trim() ? data.description : '—';
  const descLines = doc.splitTextToSize(descText, 70) as string[];
  const rowH = Math.max(16, descLines.length * 5 + 10);

  doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
  doc.setFillColor(255, 255, 255);
  doc.rect(tableX, y + headerH, tableW, rowH, 'FD');
  doc.line(tableX + 57, y + headerH, tableX + 57, y + headerH + rowH);

  const rowTextY = y + headerH + 7;
  text(doc, data.concept, tableX + 5, rowTextY, { size: 10, bold: true, color: DARK });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  doc.text(descLines, tableX + 62, rowTextY);
  text(doc, formatCurrencyPlain(data.amount), tableX + tableW - 5, rowTextY, {
    size: 11,
    bold: true,
    color: DARK,
    align: 'right',
  });

  // Total
  const totalY = y + headerH + rowH + 8;
  const totalW = 78;
  const totalX = pageWidth - margin - totalW;
  doc.setFillColor(GREEN[0], GREEN[1], GREEN[2]);
  doc.roundedRect(totalX, totalY, totalW, 14, 2, 2, 'F');
  text(doc, 'TOTAL:', totalX + 5, totalY + 9, { size: 11, bold: true, color: WHITE });
  text(doc, `Bs. ${formatCurrencyPlain(data.amount)}`, totalX + totalW - 5, totalY + 9, {
    size: 12,
    bold: true,
    color: WHITE,
    align: 'right',
  });

  // Observación
  let obsY = totalY + 26;
  if (data.observation && data.observation.trim()) {
    text(doc, 'OBSERVACIÓN', margin, obsY, { size: 8, bold: true, color: MUTED });
    obsY += 5.5;
    const obsLines = doc.splitTextToSize(data.observation, contentWidth) as string[];
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(DARK[0], DARK[1], DARK[2]);
    doc.text(obsLines, margin, obsY);
    obsY += obsLines.length * 5 + 4;
  }

  // Firmas
  const signY = 246;
  const signGap = 30;
  const signW = (contentWidth - signGap) / 2;
  const signLeftX = margin;
  const signRightX = margin + signW + signGap;
  doc.setDrawColor(DARK[0], DARK[1], DARK[2]);
  doc.setLineWidth(0.3);
  doc.line(signLeftX, signY, signLeftX + signW, signY);
  doc.line(signRightX, signY, signRightX + signW, signY);
  text(doc, 'Recibí conforme', signLeftX + signW / 2, signY + 6, {
    size: 9,
    bold: true,
    color: DARK,
    align: 'center',
  });
  text(doc, 'Entregué conforme', signRightX + signW / 2, signY + 6, {
    size: 9,
    bold: true,
    color: DARK,
    align: 'center',
  });
  text(doc, 'Firma y aclaración', signLeftX + signW / 2, signY + 11, {
    size: 8,
    color: MUTED,
    align: 'center',
  });
  text(doc, 'Firma y aclaración', signRightX + signW / 2, signY + 11, {
    size: 8,
    color: MUTED,
    align: 'center',
  });

  // Pie del recibo
  const footerY = 268;
  doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
  doc.setLineWidth(0.3);
  doc.line(margin, footerY, pageWidth - margin, footerY);
  text(doc, `Generado por: ${data.generatedBy}`, margin, footerY + 7, { size: 10, bold: true, color: DARK });
  text(doc, BRAND_FOOTER, margin, footerY + 13, { size: 9, color: MUTED });
  text(doc, `Emitido el ${formatDate(new Date())}`, pageWidth - margin, footerY + 7, {
    size: 9,
    color: MUTED,
    align: 'right',
  });

  return doc;
}

/** Descarga el recibo como PDF con el nombre estándar. */
export async function downloadReceiptPdf(data: ReceiptPdfData): Promise<void> {
  const doc = await buildReceiptPdf(data);
  doc.save(receiptFileName(data.receiptNumber));
}

/** Abre el recibo en el diálogo de impresión del navegador. */
export async function printReceiptPdf(data: ReceiptPdfData): Promise<void> {
  const doc = await buildReceiptPdf(data);
  doc.autoPrint();
  const blobUrl = doc.output('bloburl');
  window.open(blobUrl as unknown as string, '_blank');
}